import type { ApiResponse, IUser, IUserFilters, PaginatedMeta } from '@tool-no-hu/shared/types';
import api from '../lib/api';

export async function getUsersService(
  params?: { page?: number; limit?: number } & IUserFilters,
) {
  try {
    const response = await api.get<ApiResponse<PaginatedMeta & { items: IUser[] }>>('/api/v1/users', {
      params,
    });
    return response.data.data;
  } catch (error) {
    console.error('Failed to fetch users:', error);
    throw error;
  }
}

export async function getUserDetailService(id: string) {
  try {
    const response = await api.get<ApiResponse<IUser>>(`/api/v1/users/${id}`);
    return response.data.data;
  } catch (error) {
    console.error(`Failed to fetch user with id ${id}:`, error);
    throw error;
  }
}

export async function updateUserService(id: string, data: Partial<IUser>) {
  try {
    const response = await api.patch<ApiResponse<IUser>>(`/api/v1/users/${id}`, data);
    return response.data.data;
  } catch (error) {
    console.error(`Failed to update user with id ${id}:`, error);
    throw error;
  }
}

export async function updateUserPasswordService(id: string, password: string) {
  try {
    const response = await api.patch<ApiResponse<any>>(`/api/v1/users/${id}/password`, { password });
    return response.data;
  } catch (error) {
    console.error(`Failed to update password for user with id ${id}:`, error);
    throw error;
  }
}

export async function toggleUserStatusService(id: string, status: 'ACTIVE' | 'INACTIVE') {
  try {
    const response = await api.patch<ApiResponse<IUser>>(`/api/v1/users/${id}/status`, { status });
    return response.data.data;
  } catch (error) {
    console.error(`Failed to update status for user with id ${id}:`, error);
    throw error;
  }
}

export async function createUserService(data: Partial<IUser> & { password?: string; roleId?: string }) {
  try {
    const response = await api.post<ApiResponse<IUser>>('/api/v1/users', data);
    return response.data.data;
  } catch (error) {
    console.error('Failed to create user:', error);
    throw error;
  }
}

export async function getRolesService() {
  try {
    const response = await api.get<ApiResponse<any[]>>('/api/v1/users/roles');
    return response.data.data;
  } catch (error) {
    console.error('Failed to fetch roles:', error);
    throw error;
  }
}

export interface TelegramLinkedUserItem {
  _id: string;
  username: string;
  note?: string;
  telegramChatId: number;
  telegramLinkedAt: string | null;
  telegramMessageSentCount: number;
  telegramLastMessageAt: string | null;
}

export interface NotificationPreferences {
  jackpotAlert: boolean;
  jackpotMonitor: boolean;
  lowBalance: boolean;
  autoStop: boolean;
  sessionReport: boolean;
  proxyAlert: boolean;
}

export async function getTelegramLinkedUsersService(
  params?: { page?: number; limit?: number; search?: string },
) {
  try {
    const response = await api.get<ApiResponse<{ items: TelegramLinkedUserItem[]; total: number; totalCount: number; totalPages: number }>>(
      '/api/v1/users/admin/telegram-linked',
      { params },
    );
    return response.data.data;
  } catch (error) {
    console.error('Failed to fetch telegram linked users:', error);
    throw error;
  }
}

export async function getNotificationPreferencesService(): Promise<NotificationPreferences> {
  try {
    const response = await api.get<any>('/api/v1/users/notification-preferences');
    const data = response.data?.data;
    return data || { jackpotAlert: true, jackpotMonitor: true, lowBalance: true, autoStop: true, sessionReport: true, proxyAlert: true };
  } catch (error) {
    console.error('Failed to fetch notification preferences:', error);
    return { jackpotAlert: true, jackpotMonitor: true, lowBalance: true, autoStop: true, sessionReport: true, proxyAlert: true };
  }
}

export async function updateNotificationPreferencesService(
  prefs: Partial<NotificationPreferences>,
): Promise<NotificationPreferences | null> {
  try {
    const response = await api.put<ApiResponse<NotificationPreferences>>(
      '/api/v1/users/notification-preferences',
      prefs,
    );
    return (response.data.data as NotificationPreferences) || null;
  } catch (error) {
    console.error('Failed to update notification preferences:', error);
    return null;
  }
}
